import type {
  GeneratedImageResult,
  ImageGenParams,
  ImageProvider,
  ImageProviderConfig,
} from "@roqeet/core";
import { base64ToBlob } from "./openai-image.js";

export class StableDiffusionWebUiProvider implements ImageProvider {
  readonly kind = "stable-diffusion-webui";
  readonly supportsProgress = false;

  async generate(
    params: ImageGenParams,
    config: ImageProviderConfig,
    _onProgress?: (pct: number, previewBlob?: Blob) => void,
  ): Promise<GeneratedImageResult[]> {
    const base = (config.endpoint ?? "http://127.0.0.1:7860").replace(/\/+$/, "");
    const model = config.defaultModel ?? "stable-diffusion-webui";
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(config.extraHeaders ?? {}),
    };
    const body: Record<string, unknown> = {
      prompt: params.prompt,
      negative_prompt: params.negativePrompt ?? "",
      steps: params.steps ?? 20,
      cfg_scale: params.cfgScale ?? 7,
      width: params.width ?? 512,
      height: params.height ?? 512,
      batch_size: params.count ?? 1,
      ...(params.seed != null ? { seed: params.seed } : {}),
      ...(params.sampler ? { sampler_name: params.sampler } : {}),
      ...(params.extra ?? {}),
    };

    const init: RequestInit = { method: "POST", headers, body: JSON.stringify(body) };
    if (params.signal) init.signal = params.signal;

    const res = await fetch(`${base}/sdapi/v1/txt2img`, init);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Stable Diffusion WebUI image generation failed (HTTP ${res.status}): ${text.slice(0, 400)}`);
    }
    const json = (await res.json()) as { images?: string[] };
    return (json.images ?? []).map((image) => ({
      blob: base64ToBlob(stripDataUrl(image), "image/png"),
      provenance: {
        provider: config.providerId ?? this.kind,
        model,
        prompt: params.prompt,
        ...(params.negativePrompt ? { negativePrompt: params.negativePrompt } : {}),
        ...(params.seed != null ? { seed: params.seed } : {}),
        ...(params.width ? { width: params.width } : {}),
        ...(params.height ? { height: params.height } : {}),
        rawParams: body,
      },
    }));
  }
}

function stripDataUrl(value: string): string {
  const comma = value.indexOf(",");
  return value.startsWith("data:") && comma >= 0 ? value.slice(comma + 1) : value;
}
